import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { TokenService } from "@api";
import { TokenAuthServiceProxy, UserServiceProxy } from "@api/core";
import { AuthenticateModel, StartResetPasswordRequest, CompleteResetPasswordRequest } from "@api/core.models";
import { AuthenticateResultModel } from "@api/shared-models";

@Injectable()
export class AuthService {
    private _authenticated = false;

    /**
     * Constructor
     */
    constructor(
        private _tokenService: TokenService,
        private _tokenApi: TokenAuthServiceProxy,
        private _userApi: UserServiceProxy,
        private _httpClient: HttpClient) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        this._tokenService.setToken(token);
    }

    get accessToken(): string {
        return this._tokenService.getToken();
    }
    
    logout() {
        this._tokenService.clearToken();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._userApi.startResetPassword(StartResetPasswordRequest.fromJS({email}));
    }

    resetPassword(data: { token: string; password: string }): Observable<any> {
        return this._userApi.completeResetPassword(CompleteResetPasswordRequest.fromJS(data));
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        if (this._authenticated) {
            return throwError("User is already logged in.");
        }
        return this._tokenApi
            .authenticate(
                AuthenticateModel.fromJS({
                    userNameOrEmailAddress: credentials.email,
                    password: credentials.password,
                }),
            )
            .pipe(
                switchMap((response: AuthenticateResultModel) => {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    //this._userService.user = response.user;

                    // Return a new observable with the response
                    return of(response);
                }),
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient
            .post("api/auth/refresh-access-token", {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false),
                ),
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    //this._userService.user = response.user;

                    // Return true
                    return of(true);
                }),
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        this._tokenService.clearToken();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post("api/auth/sign-up", user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post("api/auth/unlock-session", credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }
        // Check the access token availability
        if (!this.accessToken) {
            console.log(4);
            return of(false);
        }

        // Check the access token expire date
        // if (AuthUtils.isTokenExpired(this.accessToken)) {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        //return this.signInUsingToken();
        return of(true);
    }

    getUserProfile(): Observable<any> {
        return of({name: "Livan Badias"});
    }
    
    isUserInRole(role: string): Observable<boolean> {
        let roles: string[] = this._tokenService.getClaim("http://schemas.microsoft.com/ws/2008/06/identity/claims/role");
        return of(roles.findIndex(r => r === role) >= 0);
    }
}
